import { z } from "zod";
import type { PayoutPolicyConfig } from "@/db/schema/payout";
import type {
  PricingComponent,
  Refundability,
  TaxRegistrationScheme,
  TaxSnapshot,
} from "@/lib/tax/types";
import type { BillingDetails } from "./billing";

/**
 * "inherit" means "follow the hiive's checkout default for this field". It is
 * only ever stored on an item that deliberately has not overridden the field —
 * the backfill freezes every pre-existing item's tax display, and the editor
 * writes a concrete value the moment an organiser picks one. Resolution
 * (`resolveCheckoutPolicy`) turns it into a concrete mode before any pricing
 * maths runs, so nothing downstream ever sees it.
 */
export const PRICING_POLICY_INHERIT = "inherit" as const;

export const pricingPolicySchema = z.object({
  v: z.literal(2),
  taxDisplayMode: z
    .enum(["included", "add_at_checkout", PRICING_POLICY_INHERIT])
    .describe(
      "Does the price entered already include tax? included=yes; add_at_checkout=no, add tax on top; inherit=use hiive default. Separate from advertised-price display.",
    ),
  feeFundingMode: z
    .enum(["organiser", "buyer", PRICING_POLICY_INHERIT])
    .describe(
      "Who pays the platform fee? organiser=deduct from your payout; buyer=add to buyer total; inherit=use hiive default.",
    ),
  taxRegistrationId: z
    .number()
    .int()
    .positive()
    .nullable()
    .describe(
      "Business registration charging this tax. Use an available registration ID; null uses the tax profile registration or the single valid registration.",
    ),
  // Discovery presentation only; never changes the supply or checkout total.
  discoveryTaxMode: z
    .enum(["included", "excluded"])
    .optional()
    .describe(
      "Include or exclude organizer tax in the advertised price on event cards and the event page, before booking. New ticket default: included. Checkout still charges applicable tax either way.",
    ),
  discoveryIncludePlatformFee: z
    .boolean()
    .optional()
    .describe(
      "Include buyer-funded platform fee, including its tax, in the advertised price. Default false. No effect when organizer pays. This only changes price display, never checkout charges.",
    ),
});
export type PricingPolicy = z.infer<typeof pricingPolicySchema>;

export const INHERITED_PRICING_POLICY: PricingPolicy = {
  v: 2,
  taxDisplayMode: PRICING_POLICY_INHERIT,
  feeFundingMode: PRICING_POLICY_INHERIT,
  taxRegistrationId: null,
};

/** Shared by Queen and MCP event creation so advertised-price defaults cannot drift. */
export const NEW_EVENT_PRICING_POLICY: PricingPolicy = {
  ...INHERITED_PRICING_POLICY,
  discoveryTaxMode: "included",
  discoveryIncludePlatformFee: false,
};

/** The two authoring choices a hiive can set once for everything it sells. */
export type CheckoutDefaults = {
  taxDisplayMode: "included" | "add_at_checkout";
  feeFundingMode: "organiser" | "buyer";
};

/**
 * The fallback a hiive ships with — and, deliberately, exactly what the code
 * hardcoded before checkout defaults existed. A hiive that never touches the
 * setting prices identically to one from before this feature.
 */
export const DEFAULT_CHECKOUT_DEFAULTS: CheckoutDefaults = {
  taxDisplayMode: "included",
  feeFundingMode: "buyer",
};

/**
 * Turn an item's stored policy (which may be null, or carry "inherit" per
 * field) into the concrete mode the pricing engine must use. The single place
 * inheritance is resolved, so a caller can never read a raw "inherit".
 */
export function resolveCheckoutPolicy(
  policy:
    | Pick<PricingPolicy, "taxDisplayMode" | "feeFundingMode">
    | null
    | undefined,
  defaults: CheckoutDefaults,
): CheckoutDefaults {
  return {
    taxDisplayMode:
      policy?.taxDisplayMode && policy.taxDisplayMode !== PRICING_POLICY_INHERIT
        ? policy.taxDisplayMode
        : defaults.taxDisplayMode,
    feeFundingMode:
      policy?.feeFundingMode && policy.feeFundingMode !== PRICING_POLICY_INHERIT
        ? policy.feeFundingMode
        : defaults.feeFundingMode,
  };
}
export type SupplierSnapshot = {
  registrationId: number;
  registrationNumber: string;
  legalName: string | null;
  legalAddress: {
    line1?: string;
    line2?: string;
    city?: string;
    subdivisionName?: string;
    postalCode?: string;
    countryCode?: string;
  } | null;
  countryCode: string;
  subdivisionCode: string | null;
  scheme: TaxRegistrationScheme;
};
export type FeeTerms = {
  policyVersionId: number | null;
  payoutConfig?: PayoutPolicyConfig;
  rateBps: number;
  taxRateBps: number;
  /** Missing on historical snapshots means tax is already inside the fee. */
  taxMode?: "included" | "added";
  fundingMode: "organiser" | "buyer";
  refundable: Refundability;
  processingFeeBps?: number;
  takeRateBps?: number;
  fixedFeeMinor?: number;
  holdbackBps?: number;
};
export type QuoteLineInput = {
  id: string;
  label?: string;
  /** Frozen India adapter jurisdiction; null means unresolved, never infer later. */
  placeOfSupplyState?: string | null;
  discountMinor?: number;
  amountMinor: number;
  quantity: number;
  priceIncludesTax: boolean;
  taxProfileId: number | null;
  components: PricingComponent[];
  supplier: SupplierSnapshot | null;
  fee?: FeeTerms;
};
export type QuotedLine = QuoteLineInput & {
  fee: FeeTerms;
  discountMinor: number;
  netMinor: number;
  chargeMinor: number;
  taxMinor: number;
  grossMinor: number;
  taxSnapshot: TaxSnapshot;
  buyerFeeMinor: number;
  buyerFeeTaxMinor: number;
  sellerFeeMinor: number;
  sellerFeeTaxMinor: number;
};
export type PricingSnapshot = {
  billingDetails?: BillingDetails;
  /** Frozen legal seller for a Hiive-owned membership period. */
  legalSeller?: "hiive";
  v: 2;
  currency: string;
  determinedAt: string;
  lines: QuotedLine[];
  fee: FeeTerms;
  displaySubtotalMinor: number;
  discountMinor: number;
  sellerNetMinor: number;
  sellerTaxMinor: number;
  sellerGrossMinor: number;
  buyerFeeMinor: number;
  buyerFeeTaxMinor: number;
  sellerFeeMinor: number;
  sellerFeeTaxMinor: number;
  totalMinor: number;
};
